import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { dmY } from "@/lib/dateFmt";
import { toast } from "sonner";
import { Warning, ArrowClockwise, ArrowUUpLeft, ArrowsClockwise, Lightbulb, CircleNotch, Broom } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

/**
 * Suspense Entries report (v1.20.11).
 *
 * Lists every transaction admin has moved to suspense from the reconcile
 * flow. Each row can be either "Restored" (back to the original party's
 * ledger) or "Move to another party" so the amount lands in the right
 * account. Suspense rows are excluded from party closing balances — this
 * is the parking area where discrepancies live until reassigned.
 */
export default function SuspenseReport() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [parties, setParties] = useState([]);
    const [totals, setTotals] = useState({ count: 0, total_amount: 0 });
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [cleaning, setCleaning] = useState(false);
    // v1.31.50 — One-click sweep of orphan Commission / Portal-Fee entries whose
    // parent payment no longer exists (moved to Suspense, moved away, or deleted)
    // in that same party. Shows a dry-run preview, then deletes on confirm.
    const cleanOrphans = async () => {
        setCleaning(true);
        try {
            const { data: dry } = await api.post("/maintenance/cleanup-orphan-charges", { dry_run: true });
            if (!dry.total_orphans) { toast.success("No orphan commission / portal-fee entries found."); return; }
            const lines = (dry.report || []).slice(0, 12)
                .map((r) => `• ${r.party_name}: ${r.orphan_count} (${fmt(r.orphan_amount)})`).join("\n");
            const more = (dry.report || []).length > 12 ? `\n…and ${dry.report.length - 12} more parties` : "";
            // Support fix (Oct 2026) — charges whose payment is in Suspense are parked
            // in Suspense with it (restorable); only charges with no payment are deleted.
            const plan = `\n\n• ${dry.to_move_to_suspense || 0} will be moved to Suspense together with their payment (restored automatically if the payment is restored)\n• ${dry.to_delete || 0} have no payment left and will be deleted permanently`;
            if (!window.confirm(`Found ${dry.total_orphans} orphan charge(s) across ${dry.parties_affected} part(y/ies):\n\n${lines}${more}${plan}\n\nContinue?`)) return;
            const { data } = await api.post("/maintenance/cleanup-orphan-charges", { dry_run: false });
            toast.success(`Done: ${data.moved_to_suspense || 0} moved to Suspense, ${data.deleted || 0} deleted, across ${data.parties_affected} part(y/ies).`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCleaning(false); }
    };
    const [moveTargets, setMoveTargets] = useState({});
    // v1.21.0 — auto-suggest destination per row (loaded lazily on click)
    const [suggests, setSuggests] = useState({}); // { txnId: { candidates, loading } }
    const loadSuggest = async (r) => {
        setSuggests((s) => ({ ...s, [r.id]: { candidates: null, loading: true } }));
        try {
            const { data } = await api.get(`/reports/suspense/${r.id}/suggest`);
            setSuggests((s) => ({ ...s, [r.id]: { candidates: data.candidates || [], loading: false } }));
        } catch (e) {
            setSuggests((s) => ({ ...s, [r.id]: { candidates: [], loading: false } }));
            toast.error(formatApiError(e));
        }
    };
    // v1.20.13 — party filter for the suspense list
    const [filterParty, setFilterParty] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const [{ data }, { data: pdata }] = await Promise.all([
                api.get("/reports/suspense"),
                api.get("/parties"),
            ]);
            setRows(data.rows || []);
            setTotals({ count: data.count || 0, total_amount: data.total_amount || 0 });
            setParties((pdata || []).filter((p) => p.active !== false));
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    // Client-side party filter — the endpoint returns all rows enriched with
    // party_id + party_name, so filtering here is instant and avoids a refetch.
    const visibleRows = React.useMemo(
        () => filterParty ? rows.filter((r) => r.party_id === filterParty) : rows,
        [rows, filterParty],
    );
    const visibleTotals = React.useMemo(() => ({
        count: visibleRows.length,
        total_amount: visibleRows.reduce((s, r) => s + Number(r.amount || 0), 0),
    }), [visibleRows]);

    // Only surface parties that actually have suspense entries in the dropdown
    const partiesWithSuspense = React.useMemo(() => {
        const ids = new Set(rows.map((r) => r.party_id));
        return parties.filter((p) => ids.has(p.id));
    }, [rows, parties]);

    const restore = async (r) => {
        if (!window.confirm(`Restore this ${r.txn_type} of ${fmt(r.amount)} back to ${r.party_name}'s ledger?`)) return;
        setBusyId(r.id);
        try {
            await api.post(`/transactions/${r.id}/suspense`, { suspense: false });
            toast.success("Restored to party ledger");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    };

    const moveToParty = async (r) => {
        const target = moveTargets[r.id];
        if (!target) { toast.error("Pick a destination party first"); return; }
        const targetName = parties.find((p) => p.id === target)?.name || target;
        if (!window.confirm(`Move this ${r.txn_type} of ${fmt(r.amount)} to "${targetName}" and clear suspense?`)) return;
        setBusyId(r.id);
        try {
            await api.patch(`/transactions/${r.id}`, { party_id: target });
            await api.post(`/transactions/${r.id}/suspense`, { suspense: false });
            toast.success(`Moved to ${targetName}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    };

    return (
        <div className="space-y-6" data-testid="suspense-report-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Reconciliation</div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight text-slate-900">Suspense Entries</h1>
                    <p className="mt-1 text-sm text-slate-600">Transactions parked here are excluded from any party's closing balance until you restore or reassign them.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={cleanOrphans} disabled={cleaning || loading} data-testid="clean-orphan-charges" className="inline-flex items-center gap-1.5 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50" title="Scan every party and delete Commission/Portal-Fee entries whose parent payment is no longer in that party's ledger">
                        {cleaning ? <CircleNotch size={14} className="animate-spin" /> : <Broom size={14} weight="fill" />} Clean Orphan Charges
                    </button>
                    <button onClick={load} disabled={loading} data-testid="suspense-refresh" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-100 disabled:opacity-50">
                        <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            {/* v1.20.13 — party filter */}
            <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <div className="min-w-[220px] flex-1 max-w-sm">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Filter by party</div>
                    <div className="mt-1">
                        <Select value={filterParty || "__all__"} onValueChange={(v) => setFilterParty(v === "__all__" ? "" : v)}>
                            <SelectTrigger data-testid="suspense-party-filter" className="h-9 text-sm">
                                <SelectValue placeholder="All parties" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All parties ({rows.length})</SelectItem>
                                {partiesWithSuspense.map((p) => {
                                    const n = rows.filter((r) => r.party_id === p.id).length;
                                    return <SelectItem key={p.id} value={p.id}>{p.name} ({n})</SelectItem>;
                                })}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                {filterParty && (
                    <button
                        onClick={() => setFilterParty("")}
                        data-testid="suspense-filter-clear"
                        className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-100"
                    >Clear filter</button>
                )}
                <div className="ml-auto text-xs text-slate-500">
                    Showing <span className="font-semibold text-slate-800">{visibleRows.length}</span> of {rows.length} entries
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-orange-300 bg-orange-100/70 p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-orange-800">In Suspense</div>
                    <div className="mt-2 font-display text-2xl font-bold text-orange-900" data-testid="suspense-count">{visibleTotals.count}</div>
                    <div className="text-[11px] text-orange-800/70">entries awaiting reassignment</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Total Amount</div>
                    <div className="mt-2 font-display text-2xl font-bold text-slate-900" data-testid="suspense-total">{fmt(visibleTotals.total_amount)}</div>
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                {loading && rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
                ) : visibleRows.length === 0 ? (
                    <div className="p-10 text-center">
                        <Warning size={28} weight="duotone" className="mx-auto text-slate-400" />
                        <div className="mt-2 font-display text-sm font-semibold text-slate-700">
                            {filterParty ? "No entries for this party" : "No entries in suspense"}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                            {filterParty ? "Try clearing the filter to see all parties." : 'Use "→ Suspense" on any unchecked row during reconcile to park it here.'}
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm" data-testid="suspense-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="px-3 py-2 text-left">Date</th>
                                    <th className="px-3 py-2 text-left">Original Party</th>
                                    <th className="px-3 py-2 text-left">Type</th>
                                    <th className="px-3 py-2 text-left">Ref / UTR</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                    <th className="px-3 py-2 text-left">Reason</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {visibleRows.map((r) => {
                                    const sug = suggests[r.id];
                                    return (
                                    <React.Fragment key={r.id}>
                                    <tr data-testid={`suspense-row-${r.id}`} className="bg-orange-100/50 hover:bg-orange-100/80">
                                        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs text-slate-700">{dmY(r.date)}</td>
                                        <td className="px-3 py-2 text-slate-900">{r.party_name}</td>
                                        <td className="px-3 py-2">
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${r.txn_type === "payment" ? "bg-emerald-100 text-emerald-800" : "bg-violet-100 text-violet-800"}`}>{r.txn_type}</span>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs text-slate-700">{r.reference || r.description || "—"}</td>
                                        <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-slate-900">{fmt(r.amount)}</td>
                                        <td className="px-3 py-2 text-xs text-slate-600">{r.suspense_reason || "—"}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => loadSuggest(r)}
                                                    disabled={sug?.loading}
                                                    data-testid={`suspense-suggest-${r.id}`}
                                                    className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                                                    title="Suggest destination party based on UTR / UPI / amount history"
                                                >
                                                    {sug?.loading ? <CircleNotch size={11} className="animate-spin" /> : <Lightbulb size={11} weight="fill" />}
                                                    {sug ? "Refresh" : "Suggest"}
                                                </button>
                                                <div className="min-w-[160px]">
                                                    <Select value={moveTargets[r.id] || ""} onValueChange={(v) => setMoveTargets((m) => ({ ...m, [r.id]: v }))}>
                                                        <SelectTrigger data-testid={`suspense-move-target-${r.id}`} className="h-8 text-xs"><SelectValue placeholder="Move to party…" /></SelectTrigger>
                                                        <SelectContent>
                                                            {parties.filter((p) => p.id !== r.party_id).map((p) => (
                                                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => moveToParty(r)}
                                                    disabled={busyId === r.id || !moveTargets[r.id]}
                                                    data-testid={`suspense-move-${r.id}`}
                                                    className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                                >
                                                    <ArrowsClockwise size={11} weight="bold" /> Move
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => restore(r)}
                                                    disabled={busyId === r.id}
                                                    data-testid={`suspense-restore-${r.id}`}
                                                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                                                    title="Restore to original party's ledger"
                                                >
                                                    <ArrowUUpLeft size={11} weight="bold" /> Restore
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {sug && !sug.loading && (
                                        <tr data-testid={`suspense-suggest-row-${r.id}`} className="bg-amber-50/20 border-b border-amber-100">
                                            <td colSpan={7} className="px-6 py-2">
                                                {sug.candidates && sug.candidates.length > 0 ? (
                                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                                        <Lightbulb size={12} weight="fill" className="text-amber-700" />
                                                        <span className="font-semibold text-amber-800">Suggested:</span>
                                                        {sug.candidates.map((c) => (
                                                            <button
                                                                key={c.party_id}
                                                                type="button"
                                                                onClick={() => setMoveTargets((m) => ({ ...m, [r.id]: c.party_id }))}
                                                                data-testid={`suspense-pick-${r.id}-${c.party_id}`}
                                                                title={c.reasons.join(" · ")}
                                                                className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                                                            >
                                                                <span className="rounded bg-amber-200 px-1 py-0.5 text-[9px] font-bold text-amber-900">{c.score}</span>
                                                                {c.party_name}
                                                                <span className="text-[10px] font-normal text-amber-700/80">· {c.reasons[0]}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-xs italic text-slate-500">No confident suggestions — pick the destination manually.</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                ); })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
